import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
} from "@mui/material";
import * as React from "react";
import { Link as RouterLink } from "react-router";

import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { SkillSummary, SkillVersionRecord } from "@/shared/domain/skills";
import { useSkillVersions } from "@/shared/domain/skills/hooks";
import { useOrgTopicNames } from "@/shared/domain/topics/hooks";
import { Button } from "@/shared/foundation/primitives";

import styles from "./SkillInspector.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The skill being inspected. */
  summary: SkillSummary | null;
  /** Specific version to pre-select when opening. Defaults to the latest
   * (MAX(version)) version. */
  initialVersion?: number | null;
  /** When set, the dialog shows an "Edit skill" link pointing here (an
   * in-app route). Used by read-only surfaces (e.g. the agent template's
   * attached-skill overlay) that don't host an editor themselves but want to
   * send the user to the skill's own page. Omit to hide the link. */
  editHref?: string;
}

function latestVersion(
  versions: SkillVersionRecord[] | undefined,
): SkillVersionRecord | null {
  if (!versions || versions.length === 0) {
    return null;
  }
  return versions.reduce((a, b) => (a.version > b.version ? a : b));
}

/**
 * Read-only peek at a skill's versions, for surfaces that reference a skill
 * without owning it — today, the agent template's attached-skill rows.
 *
 * Deliberately has no edit mode: editing a skill happens on the skill's own
 * page (`SkillDetail`), which `editHref` points at. Keeping the overlay
 * read-only means there is exactly one surface where a version's text can be
 * rewritten, rather than two that have to be kept in step.
 */
export function SkillInspector({
  open,
  onClose,
  summary,
  initialVersion,
  editHref,
}: Props) {
  const versionsQuery = useSkillVersions(
    open ? summary?.skill.skill_id : undefined,
  );
  // Org topic catalog for styling `[[topic]]` references in the body as known
  // vs not-yet-known — the same chips the editor shows while authoring. Fetched
  // only while the dialog is open; until it resolves (or if it errors), the
  // body renders without reference chips.
  const topicNamesQuery = useOrgTopicNames(summary?.skill.org_id, {
    enabled: open,
  });
  const knownTopicNames = topicNamesQuery.data;

  const [selectedVersion, setSelectedVersion] = React.useState<number | null>(
    null,
  );

  // Default the picker to the explicit ``initialVersion`` when one is passed,
  // otherwise to the live version. Reseed on every (re)open so a stale
  // selection from a previous open doesn't mis-render.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedVersion(
      initialVersion ?? summary?.latest_version?.version ?? null,
    );
  }, [
    open,
    summary?.skill.skill_id,
    summary?.latest_version?.version,
    initialVersion,
  ]);

  const versions = versionsQuery.data ?? [];
  const chosen =
    versions.find((v) => v.version === selectedVersion) ??
    latestVersion(versions);

  const title = summary
    ? `${summary.skill.name}${chosen ? ` (v${chosen.version})` : ""}`
    : "Skill";

  let bodyContent: React.ReactNode;
  if (versionsQuery.isLoading) {
    bodyContent = <CircularProgress size={20} />;
  } else if (versionsQuery.isError) {
    bodyContent = (
      <p>Couldn&apos;t load versions. Try closing and re-opening.</p>
    );
  } else if (chosen) {
    bodyContent = (
      <>
        <div>
          <strong>When to use</strong>
          <p>{chosen.description}</p>
        </div>
        <div>
          <strong>Procedure</strong>
          <div className={styles.body}>
            <RobotoMarkdown
              content={chosen.body}
              variant="compact"
              topicReferences={
                knownTopicNames !== undefined ? { knownTopicNames } : undefined
              }
            />
          </div>
        </div>
      </>
    );
  } else {
    // Defense-in-depth: every skill has ≥1 version as an invariant.
    // Reaching this branch implies a future bug, not a live state.
    bodyContent = <p>No versions to inspect.</p>;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={styles.formStack}>
          {versions.length > 1 && (
            <Select
              size="small"
              value={chosen?.version ?? ""}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
            >
              {versions.map((v) => (
                <MenuItem key={v.version} value={v.version}>
                  v{v.version}
                </MenuItem>
              ))}
            </Select>
          )}
          {bodyContent}
        </div>
      </DialogContent>
      <DialogActions>
        {editHref && (
          <RouterLink
            to={editHref}
            className={styles.editLink}
            onClick={onClose}
          >
            Edit skill
          </RouterLink>
        )}
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
